import { useCallback, useMemo, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util, Loan } from '@finley-technologies/finley-types';

const DATE_ONE_MONTH_AGO = new Date(
  new Date().setMonth(new Date().getMonth() - 1),
);

type DateRange = {
  startDate: Util.FullDateStr | null;
  endDate: Util.FullDateStr | null;
};

export type LoansFiltersDrawerState = {
  creditFacilityIds: string[];
  maturityDateRange: DateRange;
  originationDateRange: DateRange;
  lenderIds: string[];
  calculatedLoanStatuses: Loan.CalculatedLoanStatus[];
};

const hiddenLoanStatuses = new Set<Loan.CalculatedLoanStatus>([
  'pendingEdit',
  'overriddenFromEdit',
  'canceledEdit',
]);
const DEFAULT_LOAN_STATUSES = Loan.CalculatedLoanStatuses.filter((status) => {
  return !hiddenLoanStatuses.has(status);
});

export const useLoansFilters = ({
  creditFacilityIds = [],
  lenderIds = [],
  maturityDateRange = {
    startDate: commonDate.dateToFullDateString(DATE_ONE_MONTH_AGO),
    endDate: null,
  },
  originationDateRange = {
    startDate: null,
    endDate: null,
  },
  calculatedLoanStatuses = DEFAULT_LOAN_STATUSES,
}: Partial<LoansFiltersDrawerState>) => {
  // Maturity Dates
  const [
    selectedMaturityDateRange,
    setSelectedMaturityDateRange,
  ] = useState<DateRange>(maturityDateRange);
  // Origination Dates
  const [
    selectedOriginationDateRange,
    setSelectedOriginationDateRange,
  ] = useState<DateRange>(originationDateRange);
  // Facilities
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>(
    creditFacilityIds,
  );
  // Lenders
  const [selectedLenderIds, setSelectedLenderIds] = useState<string[]>(
    lenderIds,
  );
  // Loan Statuses
  const [selectedLoanStatuses, setSelectedLoanStatuses] = useState<
    Loan.CalculatedLoanStatus[]
  >(calculatedLoanStatuses);

  const currentFilters = useMemo(
    () => ({
      selectedMaturityDateRange,
      selectedOriginationDateRange,
      selectedFacilityIds,
      selectedLenderIds,
      selectedLoanStatuses,
    }),
    [
      selectedMaturityDateRange,
      selectedOriginationDateRange,
      selectedFacilityIds,
      selectedLenderIds,
      selectedLoanStatuses,
    ],
  );

  const setFilters = useCallback(
    (nextFilters: Partial<LoansFiltersDrawerState>) => {
      if (nextFilters.maturityDateRange) {
        setSelectedMaturityDateRange({
          startDate: nextFilters.maturityDateRange.startDate,
          endDate: nextFilters.maturityDateRange.endDate,
        });
      }
      if (nextFilters.originationDateRange) {
        setSelectedOriginationDateRange({
          startDate: nextFilters.originationDateRange.startDate,
          endDate: nextFilters.originationDateRange.endDate,
        });
      }
      if (nextFilters.creditFacilityIds) {
        setSelectedFacilityIds(nextFilters.creditFacilityIds);
      }
      if (nextFilters.lenderIds) {
        setSelectedLenderIds(nextFilters.lenderIds);
      }
      if (nextFilters.calculatedLoanStatuses) {
        setSelectedLoanStatuses(nextFilters.calculatedLoanStatuses);
      }
    },
    [],
  );

  return [currentFilters, setFilters] as const;
};
